/**
 * ReporteComercial — el "momento WOW" (Paso 3 del PRD) y la LLAVE comercial de Activa.
 *
 * Arco emocional (guion de reunión, PLAN-COMERCIAL §5):
 *  1. Plata dormida            → el número que DUELE (grande, "dormido", NUNCA "reactivable")
 *  2. La escalera honesta      → base histórica → dormido → recuperable estimado (piso 3%)
 *  3. Concentración de valor   → el dato que SORPRENDE
 *  4. El tamaño del problema   → la URGENCIA (la hemorragia que ya pasa)
 *  5. Los grupos               → la SOLUCIÓN / el plan
 *  + rotativo                  → "esto es sobre MÍ"
 *  + cómo lo despertamos       → honestidad que cierra (número dedicado, arranque lento, opt-out)
 *
 * REGLA MADRE (PLAN-COMERCIAL §2.2): jamás decir "reactivable" pegado al número grande.
 * Son TRES cifras que se dicen separadas: (a) base total, (b) dormido = héroe, (c) recuperable
 * = (b)×3% sub-prometido. Mostrar lo chico que es (c) es lo que genera CONFIANZA.
 *
 * Se reutiliza en el wizard (preview) y en la vista pública `/r/:token`.
 */

import { Download, Share2, PieChart, Clock, Sparkles, UserX, Moon, ShieldCheck, Repeat, Smartphone, Database, RotateCcw, AlertCircle } from "lucide-react";
import type { Grupo, ReporteComercial as Reporte } from "../types";
import type { ReportReal } from "../types.real";
import { formatARS, formatCompact } from "../mockData";
import { marcaQueFirma } from "../utils/marcaCampana";

interface Props {
  reporte: Reporte;
  grupos: Grupo[];
  /**
   * Las métricas REALES que calcula el backend (report). Van aparte porque `reporte` es el
   * tipo del mundo mock y no las tiene. Cada bloque que las usa se muestra SÓLO si el dato
   * vino: sin esto el reporte no habla de órdenes, ritmo ni canales — no los inventa.
   */
  real?: Partial<ReportReal>;
  /** true = vista pública compartida (sin acciones de operador) */
  publico?: boolean;
  /**
   * nº REAL de clientes sin datos de contacto (report.sinContacto del backend).
   * Si no viene, el bloque no se muestra: este número se le enseña al dueño del
   * local y antes caía a un valor del dataset demo (1240 inventado), que además
   * viajaba al reporte público /r/:token. Nunca inventar acá.
   */
  sinContacto?: number;
  onDescargarPDF?: () => void;
  /** true mientras el backend genera el PDF con Chromium headless (tarda unos segundos). */
  descargandoPDF?: boolean;
  onCompartir?: () => void;
  /**
   * La política de consentimiento de la marca (`GET /activa/settings` → `politicaOptIn`). Cambia qué se le puede
   * decir del alcance: con «declarada» (la de casi todas) Reactivación le escribe también a quien el archivo no dice
   * si aceptó, y el reporte decía que sólo a los que dieron su consentimiento. Sin saberla (el link público, o si no
   * se pudo leer) se dice lo que vale siempre.
   */
  politicaOptIn?: "estricta" | "declarada" | null;
}

/**
 * Días → texto en castellano, con el plural bien puesto ("1 mes", no "1 meses").
 * Los plurales por concatenación son el bug más barato de cometer y el que más rápido le baja
 * la confianza al dueño: si el reporte escribe mal, el número también parece mal calculado.
 */
function textoAntiguedad(dias: number): string {
  if (dias < 30) return dias === 1 ? "1 día" : `${dias} días`;
  const meses = Math.round(dias / 30);
  if (meses < 12) return meses === 1 ? "1 mes" : `${meses} meses`;
  const años = Math.floor(meses / 12);
  return años === 1 ? "1 año" : `${años} años`;
}

/** Meses de historia → texto. Con 1 mes decía "1 meses"; con 13, "1 años". */
function textoHistoria(meses?: number | null): string {
  if (meses == null) return "—";
  if (meses < 12) return meses === 1 ? "1 mes" : `${meses} meses`;
  const años = Math.floor(meses / 12);
  const resto = meses % 12;
  const base = años === 1 ? "1 año" : `${años} años`;
  if (!resto) return base;
  return `${base} y ${resto === 1 ? "1 mes" : `${resto} meses`}`;
}

/**
 * "2026-07-11" → { completa: "11/07/2026", corta: "11/07" }. Se parte el string a mano en vez de
 * pasar por `new Date(...)`: una fecha sin hora se parsea como UTC y en Argentina se mostraría el
 * día anterior. null si no tiene la forma esperada (no se inventa una fecha).
 */
function fechaDDMM(iso?: string): { completa: string; corta: string } | null {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { completa: `${m[3]}/${m[2]}/${m[1]}`, corta: `${m[3]}/${m[2]}` };
}

/** Un número grande con su etiqueta. La unidad de lectura del reporte. */
function Dato({ valor, label, tono = "slate" }: { valor: string; label: string; tono?: "slate" | "violeta" | "naranja" }) {
  const c = tono === "violeta" ? "text-violet-700" : tono === "naranja" ? "text-orange-600" : "text-slate-900";
  return (
    <div className="min-w-0">
      <p className={`text-2xl sm:text-3xl font-black tabular-nums tracking-tight ${c}`}>{valor}</p>
      <p className="text-[11px] text-slate-500 mt-1 leading-snug">{label}</p>
    </div>
  );
}

/** Card de comportamiento (timing / ritmo / canal). */
function Habito({ tag, icon, titulo, children }: { tag: string; icon: React.ReactNode; titulo: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="w-9 h-9 rounded-xl bg-violet-600 text-white flex items-center justify-center flex-shrink-0">{icon}</span>
        <span className="text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full bg-violet-50 text-violet-600">{tag}</span>
      </div>
      <p className="text-base font-bold text-slate-900">{titulo}</p>
      <div className="w-8 h-0.5 bg-orange-400 rounded-full my-2.5" />
      <p className="text-xs text-slate-500 leading-relaxed">{children}</p>
    </div>
  );
}

/**
 * Colores de los grupos en la barra de reparto. Son tonos -700/-600 y no los -400/-500 de
 * antes porque adentro de cada barra va el porcentaje EN BLANCO: sobre los tonos claros el
 * contraste daba 2.14–4.23:1, todos por debajo del mínimo AA (4.5:1). Medido, no estimado.
 * Con estos: emerald-700 5.48 · violet-600 5.70 · orange-700 5.18 · sky-700 5.93 · cyan-700 5.36
 * (este último, de «Activos regulares» (H76), calculado con la fórmula WCAG sobre #0e7490).
 */
const COLOR_GRUPO: Record<string, string> = {
  g_campeones: "bg-emerald-700",
  g_dormidos: "bg-violet-600",
  g_activos: "bg-cyan-700",
  g_riesgo: "bg-orange-700",
  g_nuevos: "bg-sky-700",
};

/**
 * A cuántos de los que tienen mail o celular se les puede escribir, según el consentimiento y la política de la marca.
 * `alcanzables` usa el criterio estricto (dijeron que sí); `sinConsentimiento`, los que el archivo no dice si aceptaron.
 * Antes decía siempre «hoy podés escribirle a N: son los que dieron su consentimiento», y con la política «declarada»
 * Reactivación les escribe también a los que nunca dijeron nada: el número y la promesa eran otros que el envío.
 *
 * `oficial` es a cuántos les llega por WhatsApp oficial (`alcanzablesOficial`): además del sí, un celular que el canal
 * acepte. Antes se decía «por WhatsApp oficial, sólo a los N» con N = `alcanzables`, que cuenta también a quien sólo
 * tiene mail (QA del 08/10). Un reporte guardado antes no trae el número: ahí no se dice ninguno, porque cualquiera
 * que se pusiera sería inventado o el inflado de antes.
 */
function FraseAlcance({ alcanzables, sinConsentimiento, politica, oficial }: { alcanzables: number; sinConsentimiento: number; politica?: "estricta" | "declarada" | null; oficial?: number | null }) {
  const n = (v: number) => v.toLocaleString("es-AR");
  const otros = sinConsentimiento > 0 ? sinConsentimiento : 0;
  const nOficial = typeof oficial === "number" ? oficial : null;
  /** «a 12 de ellos» / «a ninguno de ellos»: sólo si el oficial llega a menos de los que dijeron que sí */
  const oficialDeEllos = nOficial !== null && nOficial < alcanzables && (
    <>
      {" "}Por WhatsApp oficial, {nOficial === 0 ? <>a <strong>ninguno</strong> de ellos</> : <>a <strong>{n(nOficial)}</strong> de ellos</>}:
      {" "}además del sí, hace falta un celular completo, con su código de país.
    </>
  );
  if (politica === "declarada" && otros > 0) {
    return (
      <>
        De los que <strong>sí</strong> tienen mail o celular, hoy podés escribirle a{" "}
        <strong>{n(alcanzables + otros)}</strong> por tus líneas y por email: <strong>{n(alcanzables)}</strong> dieron su
        consentimiento y a otros <strong>{n(otros)}</strong> nunca se les preguntó, pero tu marca declaró tener su permiso.
        {" "}
        {nOficial !== null
          ? <>Por WhatsApp oficial, {nOficial === 0 ? <>a <strong>ninguno</strong></> : <>a <strong>{n(nOficial)}</strong></>}: Meta pide que cada persona haya dicho que sí, y hace falta un celular completo, con su código de país.</>
          : <>Por WhatsApp oficial, sólo a los que dijeron que sí y tienen celular: Meta pide que cada persona haya dicho que sí.</>}
        {" "}Pedirles el sí a esos {n(otros)} es la forma más barata de sumarlos también ahí.
      </>
    );
  }
  if (politica === "estricta" || otros === 0) {
    return (
      <>
        De los que <strong>sí</strong> tienen mail o celular, hoy podés escribirle a{" "}
        <strong>{n(alcanzables)}</strong>: son los que dieron su consentimiento.
        {oficialDeEllos}
        {otros > 0 && (
          <>
            {" "}A otros <strong>{n(otros)}</strong> nunca se les preguntó —
            no dijeron que no, así que pedirles el sí es la forma más barata de agrandar tu alcance.
          </>
        )}
      </>
    );
  }
  // sin saber la política: lo que vale siempre, sin prometer de más ni de menos
  return (
    <>
      De los que <strong>sí</strong> tienen mail o celular, <strong>{n(alcanzables)}</strong> dieron su consentimiento:
      a ellos les podés escribir.{oficialDeEllos} A otros <strong>{n(otros)}</strong> nunca se les preguntó: por WhatsApp oficial no
      (Meta pide que cada persona haya dicho que sí), y por tus líneas y por email depende del permiso que haya
      declarado tu marca. Pedirles el sí es la forma más barata de agrandar tu alcance.
    </>
  );
}

export default function ReporteComercial({ reporte, grupos, real, publico, sinContacto, onDescargarPDF, descargandoPDF, onCompartir, politicaOptIn }: Props) {
  const sinContactoN = typeof sinContacto === "number" ? sinContacto : null;
  /* Sin el nombre del comercio (o con el «Tu marca» que guardaban las campañas sin nombre) el título decía
     « · tu base de datos» o «Tu marca · tu base de datos», también en el link público y el PDF (QA del 08/10). */
  const marcaTitulo = marcaQueFirma(reporte.marca);
  const titulo = marcaTitulo ? `${marcaTitulo} · tu base de datos,` : "Tu base de datos,";

  // Los TRES números (PLAN-COMERCIAL §2.2), separados y honestos:
  const tasa = reporte.tasaRecuperoConservadora ?? 0.03;           // (c) piso conservador
  const recuperable = Math.round(reporte.plataDormida * tasa);      // (c) = (b) × 3%
  /* (a) el contexto de tamaño: cuánto facturó en toda su historia.
     `baseTotalHistorica` sólo existe en el dataset demo, así que con datos REALES la escalera se
     quedaba sin su primer escalón y el héroe se dibujaba SIEMPRE al 100% del ancho — una barra
     llena no compara con nada y el "mirá lo chico que es lo que prometemos" perdía la mitad de
     su fuerza. `facturacionTotal` es exactamente ese número y ya viene calculado del backend:
     con él, lo dormido se ve como la PORCIÓN que es (en HATSU, $27,7M sobre $69,2M) en vez de
     como un absoluto sin escala. */
  const baseTotal = reporte.baseTotalHistorica ?? real?.facturacionTotal;
  const maxVal = baseTotal ?? reporte.plataDormida;
  const barPct = (v: number) => Math.max(6, Math.round((v / maxVal) * 100));

  // Filas de pedidos que no entraron al análisis. `?? 0` porque los reportes viejos no las traen.
  const sinFecha = real?.ordenesSinFecha ?? 0;
  const fechaFutura = real?.ordenesFechaFutura ?? 0;
  /* Las ventas sin cliente van aparte: una venta a consumidor final no tiene ID y eso no se
     arregla en el archivo, así que no entran en el aviso que pide revisarlo y volver a subirlo. */
  const sinId = real?.ordenesSinId ?? 0;
  const descartadas = sinFecha + fechaFutura;
  /* Si las ventas sin cliente son más de la mitad de las filas de pedidos, lo más probable no es el
     consumidor final sino que la columna vinculada como ID no sea la del cliente (o venga vacía en
     casi todo el archivo): la nota lo sugiere, sin pedir que se vuelva a subir. */
  const sinIdEsMayoria = sinId > 0 && sinId * 2 > (real?.ordenesAnalizadas ?? 0) + descartadas + sinId;

  /* Contra qué fecha se midió todo: la última orden del archivo, no el día de hoy. Si el archivo
     terminó hace más de un mes, los que volvieron después figuran como que no volvieron — y un
     comercio que cerró por vacaciones vería a toda su base "activa". Se dice. */
  const corte = fechaDDMM(real?.fechaCorte);
  const archivoViejo = !!corte && (real?.diasDesdeUltimaOrden ?? 0) > 30;

  const escalones = [
    ...(baseTotal
      ? [{ k: "a", label: "Facturaste en toda tu historia", val: baseTotal, bar: "bg-white/25", txt: "text-violet-200/80" }]
      : []),
    { k: "b", label: `Dormido hace +${reporte.baseDormida.meses} meses`, val: reporte.plataDormida, bar: "bg-white", txt: "text-white", hero: true },
    { k: "c", label: `Recuperable estimado · piso ${Math.round(tasa * 100)}%`, val: recuperable, bar: "bg-emerald-400", txt: "text-emerald-300" },
  ];

  /* SIN NADA QUE MEDIR NO HAY REPORTE.
     Con cero órdenes legibles todos los cálculos dan 0 y el informe se dibujaba igual, entero:
     "el 0% de tus clientes genera el 0% de tu facturación", "$0 dormidos", "compró una sola vez
     0%". Cero no es un hallazgo, es la ausencia de datos — y presentarlo con la misma tipografía
     que un hallazgo real es la peor mentira posible: el dueño subió un archivo que no se pudo
     leer (columna mal vinculada, formato raro, separador distinto) y se lleva un diagnóstico
     prolijo de un negocio que no existe. Se corta acá y se dice qué revisar. */
  if (real?.ordenesAnalizadas === 0) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-6 md:p-8 animate-fade-in">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <AlertCircle size={20} />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-900 tracking-tight">
              No pudimos leer ninguna orden de tu archivo
            </h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Cargamos el archivo, pero ninguna fila tenía a la vez un cliente y una fecha que
              pudiéramos entender. Sin eso no hay nada que analizar — y preferimos decírtelo antes
              que mostrarte un reporte lleno de ceros que parezca un diagnóstico.
            </p>
            <p className="text-sm font-semibold text-slate-800 mt-4">Lo más común, en orden:</p>
            <ul className="text-sm text-slate-600 mt-1.5 space-y-1.5 list-disc pl-5 leading-relaxed">
              <li>Las columnas quedaron vinculadas al campo equivocado — volvé al paso anterior y revisá cuál es la fecha y cuál el ID del cliente.</li>
              <li>Las fechas vienen en un formato que no reconocimos. Funcionan <strong>dd/mm/aaaa</strong> y <strong>aaaa-mm-dd</strong>, con o sin hora.</li>
              <li>El archivo usa punto y coma en vez de coma para separar las columnas.</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Encabezado */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 inline-flex items-center gap-1.5">
            <Moon size={11} /> Diagnóstico de plata dormida
          </span>
          {/* h1 sólo cuando el reporte ES la página (vista pública /r/:token). Embebido en el
              wizard, la página ya tiene su h1 y su h2 de paso: emitir otro h1 acá daba dos h1 y
              un outline invertido (h2 "Reporte comercial" → h1). Con `publico` se elige el nivel
              correcto sin cambiar nada visual. */}
          {publico ? (
            <h1 className="text-2xl md:text-[2rem] leading-tight font-bold text-slate-900 mt-3 tracking-tight">
              {titulo} <span className="text-violet-600">despierta</span>
            </h1>
          ) : (
            <h2 className="text-2xl md:text-[2rem] leading-tight font-bold text-slate-900 mt-3 tracking-tight">
              {titulo} <span className="text-violet-600">despierta</span>
            </h2>
          )}
          <p className="text-sm text-slate-500 mt-1.5">
            Esto es lo que encontramos en {reporte.totalClientes === 1 ? "tu" : "tus"} <strong className="text-slate-700 font-semibold">{reporte.totalClientes.toLocaleString("es-AR")}</strong> {reporte.totalClientes === 1 ? "cliente" : "clientes"}
            {/* con un archivo de 240 decía «245 clientes» sin decir que 5 sólo aparecen en los pedidos (L8, prueba del 16/10/2026) */}
            {!!real?.huerfanos && real.huerfanos > 0 && reporte.totalClientes > real.huerfanos && (
              <> ({(reporte.totalClientes - real.huerfanos).toLocaleString("es-AR")} de tu archivo de clientes y {real.huerfanos.toLocaleString("es-AR")} {real.huerfanos === 1 ? "que sólo aparece" : "que sólo aparecen"} en tus pedidos)</>
            )}
            . Sobre datos reales, no estimaciones de marketing.
          </p>
        </div>
        {!publico && (
          <div className="flex items-center gap-2">
            <button
              onClick={onDescargarPDF}
              disabled={descargandoPDF}
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3.5 h-10 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <Download size={15} /> {descargandoPDF ? "Generando…" : "Descargar PDF"}
            </button>
            <button
              onClick={onCompartir}
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3.5 h-10 rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition-colors shadow-sm"
            >
              <Share2 size={15} /> Compartir link
            </button>
          </div>
        )}
      </div>

      {/*
        EL PUNTO DE PARTIDA — sobre qué está calculado todo esto.
        Va ANTES del número grande a propósito: un "$44M" sin decir de dónde sale pide un acto
        de fe. Diciendo primero cuántas órdenes se leyeron y de cuánto tiempo, el dueño del local
        puede contrastar contra lo que él ya sabe de su negocio — y recién ahí el número pesa.
      */}
      {real?.ordenesAnalizadas != null && (
        <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-5 sm:p-6">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 flex items-center gap-1.5 mb-4">
            <Database size={13} /> Qué miramos y cómo
            {corte && (
              <span data-testid="datos-al" className="ml-auto normal-case tracking-normal font-semibold text-slate-600">
                Datos al {corte.completa}
              </span>
            )}
          </h2>
          {archivoViejo && (
            <p data-testid="aviso-archivo-viejo" className="-mt-1 mb-4 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5 leading-relaxed">
              <strong>Tu archivo termina el {corte!.corta}: los clientes que volvieron después no aparecen.</strong>{" "}
              Todo este reporte se mide contra esa fecha. {publico ? "La foto de hoy sale de un archivo más nuevo." : "Para ver la foto de hoy, subí los archivos actualizados."}
            </p>
          )}
          <div className="grid grid-cols-3 gap-4 sm:gap-6">
            <Dato valor={(real.ordenesAnalizadas || 0).toLocaleString("es-AR")} label="pedidos analizados" tono="violeta" />
            <Dato valor={(real.clientesConCompra || 0).toLocaleString("es-AR")} label={real.clientesConCompra === 1 ? "cliente que ya te compró" : "clientes que ya te compraron"} tono="naranja" />
            <Dato valor={textoHistoria(real.mesesHistoria)} label="de historia de ventas" />
          </div>
          {/* Los compradores sin ficha. Si no lo decimos, el dueño lee "245 clientes" habiendo
              subido 240 fichas y el reporte parece tener un error nuestro. Su plata cuenta —
              existe— pero no tienen nombre ni contacto, así que ninguna campaña los alcanza. */}
          {/* Órdenes con importe ilegible. No entran en la plata (sumarlas como $0 hundiría la
              facturación y el ticket promedio), pero la compra sí ocurrió. Se dice, porque es
              plata real del negocio que este reporte NO está contando. */}
          {!!real.ordenesSinMonto && real.ordenesSinMonto > 0 && (
            <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5 leading-relaxed">
              <strong>{real.ordenesSinMonto.toLocaleString("es-AR")}</strong>{" "}
              {real.ordenesSinMonto === 1 ? "orden vino" : "órdenes vinieron"} con un importe que no pudimos leer.{" "}
              {real.ordenesSinMonto === 1 ? "Cuenta" : "Cuentan"} como compra —para saber cada cuánto vuelve la
              gente— pero {real.ordenesSinMonto === 1 ? "queda" : "quedan"} afuera de la facturación: preferimos no
              contarlas antes que contarlas como cero.
            </p>
          )}
          {/* Devoluciones y notas de crédito. Netearlas contra las ventas es contabilidad; acá
              el reporte responde "quién le compra al local", y una NC no es una compra. */}
          {!!real.ordenesNegativas && real.ordenesNegativas > 0 && (
            <p className="mt-4 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 leading-relaxed">
              <strong>{real.ordenesNegativas.toLocaleString("es-AR")}</strong>{" "}
              {real.ordenesNegativas === 1 ? "fila tenía" : "filas tenían"} importe negativo
              (devoluciones o notas de crédito). No {real.ordenesNegativas === 1 ? "se contó" : "se contaron"} como
              venta: restarlas acá haría que un cliente que devolvió algo figure gastando menos que uno que
              nunca te compró.
            </p>
          )}
          {/* Filas que NO entraron al análisis. Antes se descartaban sin que nada las contara: si
              la columna de fecha se leía mal, medio archivo desaparecía con el reporte en verde. */}
          {descartadas > 0 && (
            <p data-testid="aviso-filas-descartadas" className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5 leading-relaxed">
              <strong>{descartadas.toLocaleString("es-AR")}</strong> {descartadas === 1 ? "fila de pedidos quedó" : "filas de pedidos quedaron"} afuera del análisis:{" "}
              {[
                sinFecha > 0 && `${sinFecha.toLocaleString("es-AR")} sin fecha legible`,
                fechaFutura > 0 && `${fechaFutura.toLocaleString("es-AR")} con fecha futura`,
              ].filter(Boolean).join(" · ")}
              . Si son muchas, revisá esas columnas del archivo y volvé a subirlo.
            </p>
          )}
          {/* Ventas sin cliente asociado: quedan afuera porque no hay a quién atribuirlas, no
              porque el archivo esté mal. Se informa en gris, como las devoluciones. */}
          {sinId > 0 && (
            <p data-testid="nota-ventas-sin-cliente" className="mt-4 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 leading-relaxed">
              <strong>{sinId.toLocaleString("es-AR")}</strong> {sinId === 1 ? "venta" : "ventas"} sin cliente asociado:{" "}
              {sinId === 1 ? "no se puede" : "no se pueden"} atribuir a nadie, así que {sinId === 1 ? "queda" : "quedan"} afuera
              de este análisis. Pasa, por ejemplo, con las ventas a consumidor final.
              {sinIdEsMayoria && " Si no esperabas tantas, revisá qué columna vinculaste como ID del cliente."}
            </p>
          )}
          {/* Una columna con fechas que sólo cierran como día/mes Y otras que sólo cierran como
              mes/día no es coherente: se leyó como día/mes (el formato del país) y una parte puede
              haber quedado mal. No bloquea, pero tiene que verse. */}
          {real.formatoFecha === "mixto" && (
            <p data-testid="aviso-fechas-mixtas" className="mt-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 leading-relaxed">
              <strong>Ojo con las fechas:</strong> tu archivo mezcla fechas escritas día/mes con otras escritas mes/día. Las
              leímos todas como día/mes, así que algunas pueden haber quedado cambiadas. Conviene revisar la columna de
              fecha y exportarla en un solo formato.
            </p>
          )}
          {real.formatoFechaAsumido && real.formatoFecha !== "mixto" && (
            <p data-testid="nota-formato-asumido" className="mt-4 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 leading-relaxed">
              Ninguna fecha del archivo tenía un día mayor a 12, así que no se puede saber solo mirándolas si vienen
              día/mes o mes/día: tomamos las fechas como día/mes. Si tu sistema las exporta al revés, avisanos.
            </p>
          )}
          {!!real.huerfanos && real.huerfanos > 0 && (
            <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5 leading-relaxed">
              <strong>{real.huerfanos.toLocaleString("es-AR")}</strong>{" "}
              {real.huerfanos === 1 ? "de esos compradores aparece" : "de esos compradores aparecen"} en tus pedidos pero{" "}
              {real.huerfanos === 1 ? "no tiene" : "no tienen"} ficha en el archivo de clientes:{" "}
              {real.huerfanos === 1 ? "su compra cuenta" : "sus compras cuentan"} en la facturación, pero no{" "}
              {real.huerfanos === 1 ? "podemos escribirle" : "podemos escribirles"} porque no {real.huerfanos === 1 ? "tiene" : "tienen"} nombre ni contacto.
            </p>
          )}
          <div className="mt-5 pt-4 border-t border-slate-200/70">
            <p className="text-xs text-slate-500 leading-relaxed">
              A cada cliente lo miramos por 4 cosas simples: <strong className="text-slate-700">hace cuánto</strong> compró por
              última vez · <strong className="text-slate-700">cada cuánto</strong> vuelve · <strong className="text-slate-700">cuánto</strong> gasta
              · <strong className="text-slate-700">hace cuánto</strong> es cliente
              {real.antiguedadDias != null && (
                <> — la mitad de tus clientes te compra hace más de <strong className="text-slate-700">{textoAntiguedad(real.antiguedadDias)}</strong></>
              )}
              .
            </p>
            <p className="text-xs text-slate-500 mt-2">
              No opinamos sobre tu negocio: lo medimos. Cada número de este reporte sale de tus propias ventas — sin
              encuestas ni supuestos.
            </p>
          </div>
        </div>
      )}

      {/* HÉROE + ESCALERA HONESTA — el número que duele (b), y de dónde sale / a qué apuntamos */}
      <div className="relative overflow-hidden rounded-[1.75rem] bg-gradient-to-br from-violet-700 via-violet-800 to-indigo-950 text-white shadow-xl shadow-violet-900/20">
        <div className="absolute -right-16 -top-20 w-80 h-80 bg-violet-400/20 rounded-full blur-3xl" />
        <div className="absolute -left-10 bottom-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl" />
        <div className="relative p-8 md:p-10">
          <p className="text-xs font-bold text-violet-200 uppercase tracking-[0.18em] flex items-center gap-2">
            <Moon size={14} /> Oportunidad dormida
          </p>
          <p className="text-6xl md:text-8xl font-black tracking-tighter mt-3 tabular-nums leading-none">
            {formatCompact(reporte.plataDormida)}
          </p>
          <p className="text-base md:text-lg text-violet-100 mt-4 max-w-2xl leading-relaxed">
            No es una proyección. Es <strong className="text-white font-semibold">{formatARS(reporte.plataDormida)}</strong> que
            esta gente <strong className="text-white font-semibold">ya gastó con vos</strong> — y hoy no vuelve.
          </p>

          {/* Escalera de los 3 números: enseña la honestidad = genera confianza */}
          <div className="mt-8 pt-7 border-t border-white/15 space-y-3.5">
            {/* En mobile la fila se apila: etiqueta y valor arriba, barra abajo a lo ancho.
                En una sola línea, la etiqueta (w-48 = 192px, flex-shrink-0) y el valor se comían
                los 375px de pantalla y la barra —que es TODO el gráfico— quedaba en 0px de ancho:
                el "momento WOW" del reporte, invisible en el teléfono. */}
            {escalones.map((e) => (
              <div key={e.k} className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex items-baseline justify-between gap-3 sm:block sm:w-48 md:w-56 sm:flex-shrink-0">
                  <p className={`text-[11px] md:text-xs font-semibold ${e.hero ? "text-white" : "text-violet-200/90"} leading-tight`}>{e.label}</p>
                  {/* el valor acompaña a la etiqueta en mobile; en sm+ vuelve a su columna derecha */}
                  <p className={`sm:hidden tabular-nums font-bold ${e.txt} ${e.hero ? "text-lg" : "text-sm"}`}>{formatCompact(e.val)}</p>
                </div>
                <div className="h-2.5 rounded-full bg-white/10 overflow-hidden sm:flex-1">
                  <div className={`h-full rounded-full ${e.bar} transition-all`} style={{ width: `${barPct(e.val)}%` }} />
                </div>
                <p className={`hidden sm:block w-20 md:w-24 text-right tabular-nums font-bold ${e.txt} ${e.hero ? "text-lg" : "text-sm"}`}>{formatCompact(e.val)}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-violet-200/70 mt-5 leading-relaxed max-w-2xl">
            El <strong className="text-emerald-300">recuperable estimado</strong> va deliberadamente sub-prometido (piso del {Math.round(tasa * 100)}%): es el número que la campaña apunta a <strong className="text-white">sobre-cumplir</strong>, no a defraudar.
          </p>
        </div>
      </div>

      {/* INSIGHTS 2 y 3 — concentración (sorprende) + tamaño del problema (urgencia) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 flex items-center gap-1.5">
            <PieChart size={14} /> Concentración de valor
          </h2>
          <p className="text-4xl font-black text-slate-900 mt-3 tabular-nums flex items-baseline gap-2">
            {reporte.concentracion.pctClientes}%
            <span className="text-slate-300 font-bold text-2xl">→</span>
            {reporte.concentracion.pctFacturacion}%
          </p>
          <p className="text-sm text-slate-500 mt-2.5 leading-relaxed">
            El <strong className="text-slate-700">{reporte.concentracion.pctClientes}% de tus clientes</strong> genera el{" "}
            <strong className="text-slate-700">{reporte.concentracion.pctFacturacion}% de tu facturación</strong>. No tenés un
            problema de volumen: tenés un problema de retención de tus mejores.
          </p>
        </div>

        <div className="rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50/70 to-white p-6 shadow-sm">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-orange-500 flex items-center gap-1.5">
            <Clock size={14} /> El tamaño del problema
          </h2>
          <p className="text-4xl font-black text-orange-600 mt-3 tabular-nums">{reporte.baseDormida.pct}%</p>
          <p className="text-sm text-slate-600 mt-2.5 leading-relaxed">
            de tu base <strong>no te compra hace más de {reporte.baseDormida.meses} meses</strong>. No es un riesgo futuro: es
            una hemorragia que ya está pasando. Cada mes, un pedazo se vuelve irrecuperable.
          </p>
        </div>
      </div>

      {/*
        CÓMO SE REPARTE LA FACTURACIÓN — el "vive de un núcleo chico", hecho barra.
        La concentración de arriba dice el titular; esto muestra a quién le pertenece cada peso.
      */}
      {!!real?.repartoIngreso?.length && (
        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-4">Cómo se reparte tu facturación</h2>
          <div className="flex h-11 rounded-xl overflow-hidden">
            {real.repartoIngreso.map((g) => (
              <div
                key={g.grupoId}
                className={`${COLOR_GRUPO[g.grupoId] || "bg-slate-400"} flex items-center justify-center transition-all`}
                style={{ width: `${Math.max(g.pct, 3)}%` }}
                title={`${g.nombre}: ${g.pct}%`}
              >
                {g.pct >= 8 && <span className="text-xs font-bold text-white tabular-nums">{g.pct}%</span>}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 flex-wrap mt-3">
            {real.repartoIngreso.map((g) => (
              <span key={g.grupoId} className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                <span className={`w-2 h-2 rounded-sm ${COLOR_GRUPO[g.grupoId] || "bg-slate-400"}`} />
                {g.nombre} <span className="tabular-nums text-slate-500">{g.pct}%</span>
              </span>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4 pt-3 border-t border-slate-50 leading-relaxed">
            Perder un cliente del grupo que más factura duele mucho más que perder a cualquier otro. Ahí está la prioridad.
          </p>
        </div>
      )}

      {/*
        LA SEGUNDA COMPRA — la palanca más barata que tiene el negocio.
        Alguien que ya probó y no volvió cuesta muchísimo menos de convencer que alguien que
        todavía no te conoce: ya te eligió una vez.
      */}
      {real?.unaSolaCompra && real.unaSolaCompra.pct > 0 && (
        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center">
                <RotateCcw size={19} />
              </div>
              <div>
                <p className="text-3xl font-black text-slate-900 tabular-nums leading-none">{real.unaSolaCompra.pct}%</p>
                <p className="text-[11px] text-slate-500 mt-1">compró una sola vez</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed flex-1 min-w-[16rem]">
              <strong className="text-slate-800">{real.unaSolaCompra.cantidad.toLocaleString("es-AR")} {real.unaSolaCompra.cantidad === 1 ? "persona" : "personas"}</strong>{" "}
              {real.unaSolaCompra.cantidad === 1 ? "te probó una vez y no volvió." : "te probaron una vez y no volvieron."} Es la palanca más barata que tenés: ya te conocen y ya te eligieron. Lograr
              esa segunda compra cuesta mucho menos que salir a buscar gente nueva que te conoce de cero.
            </p>
          </div>
        </div>
      )}

      {/*
        CUÁNDO Y POR DÓNDE COMPRAN — el "esto es sobre MÍ".
        Antes era un solo insight rotativo suelto. Saber el día, el ritmo y el canal es lo que
        define cuándo y por dónde conviene mandar cada campaña, así que van juntos.
        Cada tarjeta aparece sólo si su dato existe: el canal, por ejemplo, depende de que el
        archivo de pedidos traiga esa columna.
      */}
      <div>
        <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-3">Cuándo, cómo y por dónde compran</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* el título va tal cual viene: reescribirlo acá producía "Los sábado" */}
          <Habito tag="Cuándo" icon={<Clock size={17} />} titulo={reporte.rotativo.titulo}>
            {reporte.rotativo.detalle}
          </Habito>

          {real?.ritmoDias ? (
            <Habito tag="Ritmo" icon={<Repeat size={17} />} titulo={`Cada ${real.ritmoDias} días`}>
              Es el tiempo típico entre una compra y la siguiente. Si un cliente tarda mucho más que eso, es la señal de que
              se está por ir: ahí hay que reengancharlo, no esperar.
            </Habito>
          ) : (
            <Habito tag="Ritmo" icon={<Repeat size={17} />} titulo="Todavía sin ritmo">
              Casi nadie volvió a comprar más de una vez, así que no hay un intervalo típico para medir. Ese es, justamente,
              el problema a atacar.
            </Habito>
          )}

          {real?.canalTop && (
            <Habito tag="Canal" icon={<Smartphone size={17} />} titulo={`${real.canalTop.nombre} gasta más`}>
              Los pedidos por <strong className="text-slate-700">{real.canalTop.nombre}</strong> dejan el ticket más alto:{" "}
              <strong className="text-slate-700">{formatARS(real.canalTop.ticketPromedio)}</strong> promedio sobre{" "}
              {real.canalTop.ordenes.toLocaleString("es-AR")} órdenes.
            </Habito>
          )}
        </div>
      </div>

      {/* LOS GRUPOS — la solución / el plan. Se cuentan los que vinieron (hasta 5 desde H76, y un
          grupo vacío no viaja): un «4» fijo mentía apenas el motor sumó «Activos regulares». */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
            {grupos.length === 1 ? "Tu audiencia, en 1 grupo accionable" : `Tu audiencia, en ${grupos.length} grupos accionables`}
          </h2>
          <p className="text-[11px] text-slate-500">un mensaje distinto para cada uno</p>
        </div>
        {/* clases literales (Tailwind JIT no ve strings compuestos): 5 columnas sólo si hay 5 */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${grupos.length > 4 ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-4`}>
          {grupos.map((g) => (
            <div
              key={g.id}
              className={`group rounded-2xl border border-slate-100 bg-white p-5 shadow-sm ring-1 ${g.accent.ring} hover:shadow-md transition-shadow`}
            >
              <div className={`w-11 h-11 rounded-xl ${g.accent.bg} flex items-center justify-center text-xl`}>{g.emoji}</div>
              <p className="text-sm font-bold text-slate-900 mt-3 leading-tight">{g.nombre}</p>
              <p className="text-2xl font-black text-slate-900 mt-1.5 tabular-nums">{g.personas.toLocaleString("es-AR")}</p>
              <p className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">{g.personas === 1 ? "persona" : "personas"}</p>
              {/* La oferta va abajo del monto y puede ocupar dos renglones: con 5 grupos por fila se cortaba
                  en «Cupón 20…» y el monto se partía en «$» / «23M». */}
              <div className="mt-3 pt-3 border-t border-slate-50">
                <p className={`text-sm font-bold ${g.accent.text} tabular-nums whitespace-nowrap`}>{formatCompact(g.valor)}</p>
                {g.oferta && <p className="text-[10px] font-semibold text-slate-500 mt-0.5 leading-snug">{g.oferta}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cómo los despertamos — honestidad que cierra (PLAN-COMERCIAL §5, nota de honestidad) */}
      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 flex items-start gap-3.5">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
          <ShieldCheck size={18} />
        </div>
        <div>
          <p className="text-sm font-bold text-slate-900">Cómo los despertamos, sin quemar tu marca</p>
          <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">
            Sale de un <strong className="text-slate-700">número dedicado a promos</strong> (nunca tu WhatsApp de siempre), arranca
            despacio para no gatillar bloqueos, y siempre con opción de baja. Reactivar bien es un maratón, no un sprint.
          </p>
        </div>
      </div>

      {/* Oportunidad — clientes sin datos de contacto (argumento de venta §5).
          Sólo se muestra si el backend mandó el número real. Con 0 decía «De 0 clientes no tenemos datos de contacto»
          y les pedía sumar en la caja a nadie (QA del 08/10): ahí se dice que hay de todos, y queda el alcance. */}
      {sinContactoN !== null && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex items-start gap-3.5" data-testid="bloque-contacto">
          <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0">
            <UserX size={18} />
          </div>
          <div>
            {sinContactoN > 0 ? (
              <>
                <p className="text-sm font-bold text-slate-900">
                  De {sinContactoN.toLocaleString("es-AR")} {sinContactoN === 1 ? "cliente" : "clientes"} no tenemos datos de contacto
                </p>
                <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">
                  {sinContactoN === 1
                    ? "Suma al análisis, pero no le podemos escribir. Si empezás a pedir mail o celular en la caja, lo sumás a las próximas campañas"
                    : "Suman al análisis, pero no les podemos escribir. Si empezás a pedir mail o celular en la caja, los sumás a las próximas campañas"}
                  {" "}— es plata extra esperando.
                </p>
              </>
            ) : (
              <p className="text-sm font-bold text-slate-900">Tenemos mail o celular de todos tus clientes</p>
            )}
            {/* Tener el dato no es lo mismo que poder usarlo: con consentimiento son menos. Sin esto
                el reporte prometía 225 y la campaña llegaba a 137. Los dos números cuentan a los que
                tienen mail O celular (`computeInsights`), y se dice así: WhatsApp oficial cuenta sólo a
                los que tienen celular («Bajas y consentimientos») y Resultados, sólo a los de ese envío.
                En la recorrida de QA eran 46, 55 y 45 «sin consentimiento» del mismo análisis, y con el
                mismo nombre parecían un error. */}
            {real?.alcanzables != null && (
              <p className="text-sm text-slate-700 mt-2.5 pt-2.5 border-t border-slate-100 leading-relaxed" data-testid="frase-alcance">
                <FraseAlcance alcanzables={real.alcanzables} sinConsentimiento={real.sinConsentimiento ?? 0} politica={politicaOptIn} oficial={real.alcanzablesOficial} />
              </p>
            )}
          </div>
        </div>
      )}

      {publico && (
        <div className="text-center pt-4 pb-8">
          <p className="text-xs text-slate-500">
            Reporte generado por <strong className="text-violet-600">DEENEX Activa</strong>
          </p>
        </div>
      )}
    </div>
  );
}
